import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, CalendarClock, Layers, RefreshCw, UserMinus, UserPlus, UsersRound } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { staffManagerApiService } from '../services/staffManagerApi';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { prefetchDiscordPlayers } from '../hooks/useDiscordPlayer';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { PlayerProfile } from '../components/PlayerProfile';
import { StaffAvatar } from '../components/staff/StaffAvatar';
import { HubStat, StaffHubHero, StaffHubNav } from '../components/staff/StaffHubNav';
import { TeamColourChip, teamColourStyle } from '../components/staff/manage/TeamColourChip';
import { MembershipExpiryBadge } from '../components/staff/manage/MembershipExpiryBadge';
import { TesseractAccessNotice } from '../components/staff/manage/TesseractAccessNotice';
import { AssignToTeamModal } from '../components/staff/manage/AssignToTeamModal';
import { RemoveFromTeamModal } from '../components/staff/manage/RemoveFromTeamModal';
import { MembershipExpiryModal } from '../components/staff/manage/MembershipExpiryModal';
import type { StaffCapabilities, StaffDirectoryEntry, StaffTeam, StaffTeamMember } from '../types/staffManager';
import { getStaffPerms, STAFF_PANEL_PERMISSION } from '../utils/staffPermissions';
import { formatTenure, isMembershipActive, membershipExpiryState } from '../utils/staffTenure';
import { cn } from '../utils/cn';

/** Staff Manager · Teams — Tesseract staff teams, their members, join dates, expiry and auto-granted groups. */
/** "Added by" drops out below xl so the member table fits the card instead of scrolling sideways. */
const COL_ADDED_BY = 'hidden xl:table-cell';
/** Fixed table layout: sized columns take their width, member and minecraft share the rest. */
const MEMBER_COLUMNS: { id: string; label: string; className: string }[] = [
  { id: 'member', label: 'Member', className: 'px-5' },
  { id: 'minecraft', label: 'Minecraft', className: 'px-4' },
  { id: 'joined', label: 'Joined', className: 'px-4 w-[130px]' },
  { id: 'expiry', label: 'Expiry', className: 'px-4 w-[150px]' },
  { id: 'added_by', label: 'Added by', className: `px-4 w-[160px] ${COL_ADDED_BY}` },
  { id: 'actions', label: '', className: 'px-5 w-[130px] xl:w-[220px]' },
];

export const StaffTeamsView = () => {
  useDocumentTitle('Staff Teams');
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;
  const perms = useMemo(() => getStaffPerms(identity, isDev), [identity, isDev]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [capabilities, setCapabilities] = useState<StaffCapabilities | null>(() => staffManagerApiService.peek<StaffCapabilities>('capabilities'));
  const [teams, setTeams] = useState<StaffTeam[] | null>(() => staffManagerApiService.peek<{ teams: StaffTeam[] }>('teams')?.teams ?? null);
  const [directory, setDirectory] = useState<StaffDirectoryEntry[]>(() => staffManagerApiService.peek<{ staff: StaffDirectoryEntry[] }>('directory')?.staff ?? []);
  const [loading, setLoading] = useState(teams === null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{ team: StaffTeam; member: StaffTeamMember } | null>(null);
  const [expiryTarget, setExpiryTarget] = useState<{ team: StaffTeam; member: StaffTeamMember } | null>(null);

  const load = useCallback(async (force = false) => {
    if (force) setRefreshing(true);
    setError(null);
    const [capRes, teamsRes, dirRes] = await Promise.allSettled([
      staffManagerApiService.getCapabilities({ force }),
      staffManagerApiService.getTeams({ force }),
      staffManagerApiService.getDirectory({ force }),
    ]);
    if (capRes.status === 'fulfilled') setCapabilities(capRes.value);
    if (teamsRes.status === 'fulfilled') {
      setTeams(teamsRes.value.teams);
    } else {
      setError(teamsRes.reason instanceof Error ? teamsRes.reason.message : 'Failed to load teams');
    }
    if (dirRes.status === 'fulfilled') {
      setDirectory(dirRes.value.staff);
      void prefetchDiscordPlayers(dirRes.value.staff.map((s) => s.discord_id).filter((d): d is string => !!d));
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    if (perms.canView) void load();
  }, [perms.canView, load]);

  const selectedId = searchParams.get('team') ?? teams?.[0]?.id ?? '';
  const selected = useMemo(() => teams?.find((t) => t.id === selectedId) ?? teams?.[0] ?? null, [teams, selectedId]);
  const selectTeam = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('team', id);
    setSearchParams(next);
  };

  const directoryById = useMemo(() => new Map(directory.map((e) => [e.user_id, e])), [directory]);
  const now = Date.now();
  const canEdit = perms.canManage && !!capabilities?.tesseract.connected && capabilities.tesseract.can_edit_teams;
  const selfId = capabilities?.tesseract.user_id?.toLowerCase();

  const stats = useMemo(() => {
    if (!teams) return null;
    const members = new Set<string>();
    let expiring = 0;
    let groups = 0;
    for (const t of teams) {
      groups += t.groups.length;
      for (const m of t.members) {
        if (!isMembershipActive(m, now)) continue;
        members.add(m.user_id);
        if (membershipExpiryState(m.expires_at, now).state === 'expiring') expiring += 1;
      }
    }
    return { teams: teams.length, members: members.size, expiring, groups };
  }, [teams, now]);

  const activeMembers = useMemo(() => {
    if (!selected) return [];
    return selected.members
      .filter((m) => isMembershipActive(m, now))
      .map((m) => ({ member: m, entry: directoryById.get(m.user_id) ?? null }))
      .sort((a, b) => (a.member.assigned_at ?? 0) - (b.member.assigned_at ?? 0));
  }, [selected, directoryById, now]);

  if (!perms.canView) {
    return (
      <div className="glass-card p-8 text-center">
        <p className="text-glass-300">You don't have permission to view the Staff Manager.</p>
        <p className="text-glass-500 text-sm mt-1">
          Ask an admin for the <code>{STAFF_PANEL_PERMISSION}</code> permission.
        </p>
      </div>
    );
  }

  const resolveName = (m: StaffTeamMember, entry: StaffDirectoryEntry | null) => entry?.minecraft_username ?? entry?.discord_username ?? m.username;
  const assignedByName = (id: string | null) => (id ? directoryById.get(id)?.username ?? `${id.slice(0, 8)}…` : 'seed / unknown');

  return (
    <div className="space-y-6">
      <StaffHubHero
        title="Staff Teams"
        icon={UsersRound}
        blurb="Tesseract staff teams, who is on them, since when, and until when. Adding or removing a member here changes Tesseract directly — with your own Tesseract permissions."
        nav={<StaffHubNav perms={perms} />}
        actions={
          <>
            {canEdit && teams && teams.length > 0 && (
              <button type="button" onClick={() => setAssignOpen(true)} className="glass-button inline-flex items-center gap-2 text-sm">
                <UserPlus className="w-4 h-4" />
                Add member
              </button>
            )}
            <button type="button" onClick={() => void load(true)} className="glass-button-secondary inline-flex items-center gap-2 text-sm" disabled={refreshing || loading}>
              <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
              Refresh
            </button>
          </>
        }
        stats={
          stats ? (
            <>
              <HubStat label="Teams" value={stats.teams} sub="in Tesseract" />
              <HubStat label="Members" value={stats.members} sub="unique active memberships" tone="text-green-300" />
              <HubStat label="Expiring soon" value={stats.expiring} sub="within 14 days" tone={stats.expiring > 0 ? 'text-amber-300' : 'text-glass-400'} />
              <HubStat label="Auto-granted groups" value={stats.groups} sub="team → group links" tone="text-purple-300" />
            </>
          ) : undefined
        }
      />

      {perms.canManage ? <TesseractAccessNotice capabilities={capabilities} needs="role.edit" /> : <TesseractAccessNotice capabilities={capabilities} needs="role.view" />}

      {error && (
        <div className="glass-card p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Failed to load teams</h2>
          <p className="text-glass-400">{error}</p>
          <button onClick={() => void load(true)} className="glass-button mt-4">
            Retry
          </button>
        </div>
      )}

      {loading && !error && (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3">
          {Array.from({ length: 6 }, (_, i) => (
            <SkeletonLoader key={i} className="h-28 w-full" />
          ))}
        </div>
      )}

      {teams && !loading && !error && (
        <>
          {/* Team cards */}
          <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3" role="tablist" aria-label="Teams">
            {teams.map((t) => {
              const active = t.members.filter((m) => isMembershipActive(m, now));
              const expiring = active.filter((m) => membershipExpiryState(m.expires_at, now).state === 'expiring').length;
              const isSelected = selected?.id === t.id;
              const colour = teamColourStyle(t.color);
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={isSelected}
                  onClick={() => selectTeam(t.id)}
                  className={cn(
                    'glass-card relative overflow-hidden text-left p-4 transition-colors touch-manipulation',
                    isSelected ? 'ring-1 ring-accent-400/50 bg-accent-500/5' : 'hover:border-accent-400/30',
                  )}
                >
                  <div className="absolute inset-x-0 top-0 h-1" style={colour ? { backgroundColor: colour.color as string } : undefined} aria-hidden="true">
                    {!colour && <div className="h-full bg-gradient-to-r from-accent-500/60 to-accent-300/60" />}
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-white font-semibold truncate">{t.display_name}</div>
                      <div className="text-[11px] font-mono text-glass-500 truncate">
                        {t.name}
                        {t.domain !== 'playcdu' && ` · ${t.domain}`}
                      </div>
                    </div>
                    <span className="text-2xl font-bold text-white tabular-nums leading-none">{active.length}</span>
                  </div>
                  {t.description && <p className="text-xs text-glass-400 mt-2 line-clamp-2">{t.description}</p>}
                  <div className="mt-3 flex flex-wrap items-center gap-1">
                    {t.groups.slice(0, 3).map((g) => (
                      <span key={g.id} className="inline-flex items-center h-5 px-1.5 rounded text-[10px] font-mono bg-glass-100/10 text-glass-300 border border-glass-200/15">
                        {g.name}
                      </span>
                    ))}
                    {t.groups.length > 3 && <span className="text-[10px] text-glass-500">+{t.groups.length - 3}</span>}
                    {expiring > 0 && (
                      <span className="ml-auto inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30">
                        <CalendarClock className="w-3 h-3" /> {expiring} expiring
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Selected team */}
          {selected && (
            <section className="glass-card relative overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent-400/50 to-transparent" aria-hidden="true" />
              <header className="flex flex-wrap items-center gap-3 px-4 pt-4 md:px-5 md:pt-5">
                <TeamColourChip team={selected} size="md" showDomain />
                <h2 className="text-sm font-semibold text-white tracking-wide">
                  Members <span className="ml-1 text-xs font-mono font-normal text-glass-400 tabular-nums">{activeMembers.length}</span>
                </h2>
                {selected.groups.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-glass-500">
                    <Layers className="w-3.5 h-3.5" /> grants {selected.groups.map((g) => g.name).join(', ')}
                  </span>
                )}
                {canEdit && (
                  <button type="button" onClick={() => setAssignOpen(true)} className="ml-auto glass-button-sm inline-flex items-center gap-1.5">
                    <UserPlus className="w-3.5 h-3.5" /> Add to {selected.display_name}
                  </button>
                )}
              </header>

              <div className="px-4 pb-4 pt-3 md:px-5 md:pb-5">
                {activeMembers.length === 0 ? (
                  <div className="p-8 text-center text-sm text-glass-500">No active members.</div>
                ) : (
                  <>
                    <div className="hidden lg:block overflow-x-auto -mx-4 md:-mx-5">
                      <table className="w-full table-fixed">
                        <thead>
                          <tr className="border-b border-glass-200/15">
                            {MEMBER_COLUMNS.filter((c) => c.id !== 'actions' || canEdit).map((c) => (
                              <th key={c.id} className={cn('text-left text-[11px] uppercase tracking-[0.14em] text-glass-500 font-semibold py-2.5 whitespace-nowrap', c.className)}>
                                {c.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {activeMembers.map(({ member, entry }) => {
                            const isSelf = selfId !== undefined && member.user_id.toLowerCase() === selfId;
                            return (
                              <tr key={member.user_id} className="border-b border-glass-200/10 hover:bg-accent-500/5 transition-colors">
                                <td className="px-5 py-3">
                                  <div className="flex items-center gap-3 min-w-0">
                                    {entry?.discord_id ? (
                                      <StaffAvatar discordId={entry.discord_id} name={resolveName(member, entry)} />
                                    ) : (
                                      <span className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-500/30 to-accent-700/20 inline-flex items-center justify-center font-bold text-accent-200" aria-hidden="true">
                                        {(resolveName(member, entry)[0] || '?').toUpperCase()}
                                      </span>
                                    )}
                                    <div className="min-w-0">
                                      {entry?.discord_id ? (
                                        <Link to={`/staff/${entry.discord_id}`} className="text-white font-semibold truncate hover:text-accent-300 transition-colors">
                                          {resolveName(member, entry)}
                                        </Link>
                                      ) : (
                                        <div className="text-white font-semibold truncate">{resolveName(member, entry)}</div>
                                      )}
                                      <div className="text-[11px] font-mono text-glass-400 truncate">
                                        @{member.username}
                                        {isSelf && <span className="text-accent-300"> · you</span>}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  {entry?.minecraft_uuid ? (
                                    <PlayerProfile uuid={entry.minecraft_uuid} name={entry.minecraft_username ?? undefined} showUuid={false} inline />
                                  ) : (
                                    <span className="text-xs text-glass-500 italic">Not linked</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {member.assigned_at ? (
                                    <>
                                      <div className="text-sm text-white font-semibold tabular-nums">{formatTenure(member.assigned_at, now)}</div>
                                      <div className="text-[11px] text-glass-500">{format(new Date(member.assigned_at), 'd MMM yyyy')}</div>
                                    </>
                                  ) : (
                                    <span className="text-xs text-glass-500">—</span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <MembershipExpiryBadge expiresAt={member.expires_at} showPermanent />
                                </td>
                                <td className={cn('px-4 py-3 text-xs text-glass-400 font-mono whitespace-nowrap', COL_ADDED_BY)}>{assignedByName(member.assigned_by)}</td>
                                {canEdit && (
                                  <td className="px-5 py-3 text-right">
                                    {isSelf ? (
                                      <span className="text-[11px] text-glass-500" title="You cannot change your own membership from ARGUS">
                                        —
                                      </span>
                                    ) : (
                                      <span className="inline-flex flex-wrap justify-end items-center gap-1">
                                        <button type="button" onClick={() => setExpiryTarget({ team: selected, member })} className="glass-button-sm inline-flex items-center gap-1" title="Set expiry">
                                          <CalendarClock className="w-3.5 h-3.5" /> Expiry
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => setRemoveTarget({ team: selected, member })}
                                          className="glass-button-sm inline-flex items-center gap-1 text-red-300 hover:bg-red-500/10"
                                          title="Remove from team"
                                        >
                                          <UserMinus className="w-3.5 h-3.5" /> Remove
                                        </button>
                                      </span>
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="lg:hidden space-y-2.5">
                      {activeMembers.map(({ member, entry }) => {
                        const isSelf = selfId !== undefined && member.user_id.toLowerCase() === selfId;
                        return (
                          <div key={member.user_id} className="glass-card p-3 space-y-2">
                            <div className="flex items-center gap-3">
                              {entry?.discord_id ? <StaffAvatar discordId={entry.discord_id} name={resolveName(member, entry)} /> : null}
                              <div className="min-w-0 flex-1">
                                <div className="font-semibold text-white truncate">{resolveName(member, entry)}</div>
                                <div className="text-[11px] font-mono text-glass-400 truncate">
                                  @{member.username} · {member.assigned_at ? `joined ${formatDistanceToNow(new Date(member.assigned_at), { addSuffix: true })}` : 'join date unknown'}
                                </div>
                              </div>
                              <MembershipExpiryBadge expiresAt={member.expires_at} />
                            </div>
                            {canEdit && !isSelf && (
                              <div className="flex gap-2">
                                <button type="button" onClick={() => setExpiryTarget({ team: selected, member })} className="glass-button-sm flex-1 inline-flex items-center justify-center gap-1">
                                  <CalendarClock className="w-3.5 h-3.5" /> Expiry
                                </button>
                                <button type="button" onClick={() => setRemoveTarget({ team: selected, member })} className="glass-button-sm flex-1 inline-flex items-center justify-center gap-1 text-red-300">
                                  <UserMinus className="w-3.5 h-3.5" /> Remove
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </section>
          )}
        </>
      )}

      <AssignToTeamModal isOpen={assignOpen} onClose={() => setAssignOpen(false)} teams={teams ?? []} defaultTeamId={selected?.id ?? null} onSuccess={() => void load(true)} />
      <RemoveFromTeamModal
        isOpen={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        team={removeTarget?.team ?? null}
        member={removeTarget?.member ?? null}
        teams={teams ?? []}
        onSuccess={() => void load(true)}
      />
      <MembershipExpiryModal
        isOpen={expiryTarget !== null}
        onClose={() => setExpiryTarget(null)}
        team={expiryTarget?.team ?? null}
        member={expiryTarget?.member ?? null}
        onSuccess={() => void load(true)}
      />
    </div>
  );
};
